import React from 'react';
import { motion } from 'framer-motion';
import './OrderDisplay.css';

function OrderDisplay({ order, scannedItems, mode, onItemTap }) {
  if (!order) return null;

  // Calculate progress for each item
  const getItemProgress = (item) => {
    const scannedQty = scannedItems.filter(s => s.sku === item.sku).length;
    const totalQty = Number(item.quantity) || 0;
    return {
      scanned: scannedQty,
      total: totalQty,
      percentage: totalQty > 0 ? (scannedQty / totalQty) * 100 : 0,
      complete: scannedQty === totalQty
    };
  };

  // Calculate overall progress
  const totalItems = order.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalScanned = scannedItems.length;
  const overallProgress = totalItems > 0 ? (totalScanned / totalItems) * 100 : 0;

  return (
    <motion.div
      className="order-display"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Compact Order Header - all on one line */}
      <div className="order-header-compact">
        <span className="order-number">#{order.orderNumber}</span>
        <span className="customer-name">{order.customerName}</span>
        <span className="order-date">{new Date(order.orderDate).toLocaleDateString()}</span>
        <div className="progress-compact">
          <span className="progress-count">{totalScanned}/{totalItems}</span>
          <div className="progress-bar-mini">
            <motion.div
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.5 }}
              style={{
                backgroundColor: overallProgress === 100 ? '#4CAF50' : '#2196F3'
              }}
            />
          </div>
        </div>
      </div>

      {/* Items List - Compact */}
      <div className="items-section">
        <div className="items-list">
          {order.items.map((item, index) => {
            const progress = getItemProgress(item);

            return (
              <motion.div
                key={item.itemId || index}
                className={`order-item ${progress.complete ? 'complete' : ''} ${progress.scanned > 0 ? 'in-progress' : ''} ${onItemTap ? 'tappable' : ''}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => onItemTap && !progress.complete && onItemTap(item, progress)}
              >
                <div className="item-line-compact">
                  <div className="item-info-compact">
                    <span className="item-sku">{item.sku}</span>
                    <span className="item-description">{item.description}</span>
                  </div>
                  <div className="item-qty-compact">
                    <span className="qty-text">{progress.scanned}/{progress.total}</span>
                    {progress.complete && (
                      <motion.div
                        className="check-icon-small"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                      >
                        ✓
                      </motion.div>
                    )}
                  </div>
                </div>
                <div className="item-progress-mini">
                  <motion.div
                    className="item-progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress.percentage}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Special Instructions / Memo, with the customer's gift note pulled
          out into its own prominent block. The gift note arrives inside the
          order note/NS memo on a "GIFT NOTE:" line. */}
      {(() => {
        const raw = order.specialInstructions || order.memo || '';
        const giftMatch = raw.match(/GIFT NOTE:\s*([\s\S]*)/i);
        const giftNote = giftMatch ? giftMatch[1].trim() : null;
        const instructions = giftMatch ? raw.slice(0, giftMatch.index).trim() : raw.trim();

        const printGiftNote = () => {
          const w = window.open('', '_blank', 'width=500,height=400');
          w.document.write(`<html><head><title>Gift note</title><style>
            body { font-family: Georgia, 'Times New Roman', serif; color: #312f14;
                   display: flex; align-items: center; justify-content: center;
                   min-height: 90vh; margin: 0; padding: 2em; }
            .card { max-width: 4.5in; text-align: center; }
            .card p { font-size: 15pt; line-height: 1.6; white-space: pre-line; margin: 0 0 1.5em; }
            .brand { font-family: Arial, sans-serif; font-size: 9pt; letter-spacing: 0.35em;
                     text-transform: uppercase; color: #8d8568; }
          </style></head><body><div class="card"><p>${giftNote
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
            <div class="brand">Rewined</div></div></body></html>`);
          w.document.close();
          w.focus();
          w.print();
        };

        return (
          <>
            {giftNote && (
              <div className="special-instructions" style={{ border: '2px solid #B2A13F', background: '#FBF8EC' }}>
                <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>GIFT NOTE — include with order</span>
                  <button onClick={printGiftNote} style={{ fontSize: '0.8rem', padding: '4px 12px', cursor: 'pointer' }}>
                    Print note card
                  </button>
                </h3>
                <p style={{ fontStyle: 'italic' }}>{giftNote}</p>
              </div>
            )}
            {instructions && (
              <div className="special-instructions">
                <h3>Special Instructions / Memo</h3>
                <p>{instructions}</p>
              </div>
            )}
          </>
        );
      })()}
    </motion.div>
  );
}

export default OrderDisplay;